import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { api, resolveImg, isNew } from "@/lib/adminApi";
import ProductBadges from "@/components/jd/ProductBadges";

const MotionLink = motion(Link);

const META = {
  whiskey: { label: "Whiskey", tagline: "The malts, the bourbons, the quiet legends." },
  spirits: { label: "Other Spirits", tagline: "Cognac, mezcal, rum — the neighbouring nations." },
  glassware: { label: "Glassware", tagline: "Tulips, tumblers, decanters. Vessels of ceremony." },
  apparel: { label: "Apparel", tagline: "Tweed, wool, leather. The uniform of the evening." },
  barware: { label: "Barware", tagline: "Jiggers, ice, coasters — the small tools of a proper pour." },
};

export default function ShelfCategory() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("recent"); // 'recent' | 'score'
  const meta = META[category];
  const scoreable = category === "whiskey" || category === "spirits";

  const sortedItems = useMemo(() => {
    if (sortBy === "score" && scoreable) {
      return [...items].sort((a, b) => {
        const sa = a.score == null ? -1 : Number(a.score);
        const sb = b.score == null ? -1 : Number(b.score);
        return sb - sa;
      });
    }
    return items; // backend already returns newest-first
  }, [items, sortBy, scoreable]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!meta) {
      navigate("/", { replace: true });
      return;
    }
    setLoading(true);
    setSortBy("recent");
    api
      .get(`/picks`, { params: { category } })
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [category, meta, navigate]);

  if (!meta) return null;

  return (
    <div className="grain vignette bg-bg text-bone min-h-screen">
      {/* Top bar */}
      <header className="border-b sticky top-0 z-30 nav-blur" style={{ borderColor: "var(--hairline)" }}>
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="shelf-logo" className="flex items-center gap-3">
            <svg width="26" height="26" viewBox="0 0 40 40" fill="none">
              <path d="M8 8 L20 32 L32 8" stroke="var(--brass)" strokeWidth="1.4" />
              <circle cx="20" cy="20" r="3" fill="var(--amber)" />
            </svg>
            <span className="font-display text-[1.1rem] tracking-wide">Johnny Dram</span>
          </Link>
          <Link to="/#top-shelf" data-testid="shelf-back" className="btn-outline">
            <ArrowLeft size={16} /> Top Shelf
          </Link>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 sm:px-10 py-20 md:py-28">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 lg:col-span-8">
            <div className="eyebrow mb-4">Top Shelf · The Full Cabinet</div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-6xl md:text-8xl leading-[0.92] tracking-tight text-bone"
            >
              {meta.label}<span style={{ color: "var(--amber)" }}>.</span>
            </motion.h1>
          </div>
          <div className="col-span-12 lg:col-span-4 flex items-end">
            <p className="font-body text-lg text-bone-dim">{meta.tagline}</p>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4 mb-12">
          <div className="hairline flex-1 min-w-[80px]" />
          {scoreable && items.length > 1 && (
            <div className="flex items-center gap-2" data-testid="shelf-sort">
              <span className="eyebrow mr-1" style={{ color: "var(--bone-dim)" }}>Sort</span>
              <button
                onClick={() => setSortBy("recent")}
                data-testid="sort-recent"
                className="px-4 py-2 font-accent uppercase tracking-widest text-xs border transition-colors"
                style={{
                  borderColor: "var(--hairline)",
                  background: sortBy === "recent" ? "var(--surface-2)" : "transparent",
                  color: sortBy === "recent" ? "var(--bone)" : "var(--bone-dim)",
                }}
              >
                Most Recent
              </button>
              <button
                onClick={() => setSortBy("score")}
                data-testid="sort-score"
                className="px-4 py-2 font-accent uppercase tracking-widest text-xs border transition-colors"
                style={{
                  borderColor: sortBy === "score" ? "var(--amber)" : "var(--hairline)",
                  background: sortBy === "score" ? "var(--surface-2)" : "transparent",
                  color: sortBy === "score" ? "var(--bone)" : "var(--bone-dim)",
                }}
              >
                Highest Rated
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-24 text-center eyebrow" style={{ color: "var(--bone-dim)" }}>Pulling bottles from the cellar…</div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center">
            <p className="font-display text-3xl text-bone-dim mb-6">Nothing on this shelf yet.</p>
            <Link to="/#top-shelf" className="btn-outline">Back to Top Shelf</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedItems.map((item, idx) => {
              const reviewable = item.category === "whiskey" || item.category === "spirits";
              const common = {
                "data-testid": `shelf-item-${item.id}`,
                initial: { opacity: 0, y: 24 },
                animate: { opacity: 1, y: 0 },
                transition: { duration: 0.5, delay: Math.min(idx * 0.05, 0.4) },
                className: "tile-frame group flex flex-col",
              };
              const inner = (
                <>
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={resolveImg(item.image_url)} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4 eyebrow" style={{ color: "var(--bone)" }}>
                    {meta.label} · N°{String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="absolute top-4 right-4 w-9 h-9 border rounded-full flex items-center justify-center" style={{ borderColor: "var(--brass)", background: "rgba(14,13,11,0.35)" }}>
                    <ArrowUpRight size={14} color="var(--bone)" />
                  </div>
                  {isNew(item.created_at) && (
                    <span className="new-badge absolute bottom-4 left-4" data-testid={`shelf-new-${item.id}`}>
                      <span className="dot" /> New
                    </span>
                  )}
                  {reviewable && item.score != null && (
                    <span className="score-badge absolute bottom-4 right-4" data-testid={`shelf-score-${item.id}`}>
                      <span className="val">{Number(item.score) % 1 === 0 ? Number(item.score).toFixed(0) : Number(item.score)}</span>
                      <span className="max">/10</span>
                    </span>
                  )}
                  <ProductBadges item={item} size={46} />
                </div>
                <div className="p-5 flex items-start justify-between gap-4 border-t" style={{ borderColor: "var(--hairline)" }}>
                  <div className="min-w-0">
                    <div className="font-display text-xl leading-tight text-bone truncate">{item.name}</div>
                    <div className="font-body text-sm text-bone-dim mt-1">{item.detail}</div>
                    {reviewable && <div className="eyebrow mt-2" style={{ color: "var(--amber)" }}>Read Review →</div>}
                  </div>
                  <div className="font-accent text-brass shrink-0" style={{ letterSpacing: "0.1em" }}>{item.price}</div>
                </div>
                </>
              );
              return reviewable ? (
                <MotionLink key={item.id} to={`/review/${item.slug || item.id}`} {...common}>{inner}</MotionLink>
              ) : (
                <motion.a key={item.id} href={item.affiliate_url} target="_blank" rel="noopener noreferrer sponsored" {...common}>{inner}</motion.a>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
